<script setup>
  import { computed } from 'vue'
  import { sanitizeHtml } from '../../utils/sanitizeHtml'

  const props = defineProps({
    bodyHtml: { type: String, default: '' }
  })

  // bcst_lessons is admin-only writable and bodyHtml comes from the CMS editor, but we
  // still sanitize before v-html as defense-in-depth (strips scripts, event handlers,
  // and unsafe URLs while preserving formatting).
  const safeBodyHtml = computed(() => sanitizeHtml(props.bodyHtml))

  const hasContent = computed(() => safeBodyHtml.value.trim().length > 0)
</script>

<template>
  <div v-if="hasContent" class="blc-container">
    <h2 class="blc-title">Video Description:</h2>
    <div class="blc-content" v-html="safeBodyHtml"></div>
  </div>
</template>

<style scoped lang="scss">
  .blc-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 874px;
    max-width: 100%;
    margin-top: 31px;
    margin-bottom: 48px;
  }

  .blc-title {
    color: var(--Gray-700, #495057);
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 130%; /* 23.4px */
    letter-spacing: -0.36px;
    margin: 0;
  }

  .blc-content {
    color: var(--Gray-700, #495057);
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 130%;
    letter-spacing: -0.32px;

    // Paragraphs
    :deep(p) {
      margin: 0 0 16px 0;
      color: var(--Gray-700, #495057);
      font-family: "DM Sans";
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 130%; /* 20.8px */
      letter-spacing: -0.32px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    // Headings
    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4),
    :deep(h5),
    :deep(h6) {
      font-family: 'DM Sans', sans-serif;
      font-weight: 700;
      line-height: 130%;
      margin: 24px 0 12px 0;
      color: var(--Gray-700, #495057);
      font-size: 18px;
      letter-spacing: -0.36px;

      &:first-child {
        margin-top: 0;
      }
    }

    // Strong and emphasis
    :deep(strong),
    :deep(b) {
      font-weight: 700;
      color: var(--bs-gray-900);
    }

    :deep(em),
    :deep(i) {
      font-style: italic;
    }

    // Links
    :deep(a) {
      color: var(--bs-primary);
      text-decoration: none;
      font-weight: 600;
      transition: all 0.2s ease;

      &:hover {
        text-decoration: underline;
      }

      &:focus,
      &:focus-visible {
        outline: 2px solid var(--bs-primary);
        outline-offset: 2px;
      }
    }

    // Lists
    :deep(ul),
    :deep(ol) {
      margin: 0 0 16px 0;
      padding-left: 24px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    :deep(li) {
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    :deep(ul) {
      list-style-type: disc;
    }

    :deep(ol) {
      list-style-type: decimal;
    }

    // Nested lists
    :deep(ul ul),
    :deep(ol ol),
    :deep(ul ol),
    :deep(ol ul) {
      margin-top: 8px;
      margin-bottom: 8px;
    }

    // Blockquotes
    :deep(blockquote) {
      margin: 20px 0;
      padding: 12px 20px;
      border-left: 4px solid var(--bs-primary);
      background-color: var(--bs-gray-100);
      font-style: italic;
      color: var(--bs-gray-700);
    }

    // Code
    :deep(code) {
      padding: 2px 6px;
      background-color: var(--bs-gray-100);
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 14px;
      color: var(--bs-gray-900);
    }

    :deep(pre) {
      margin: 16px 0;
      padding: 16px;
      background-color: var(--bs-gray-100);
      border-radius: 8px;
      overflow-x: auto;

      code {
        padding: 0;
        background-color: transparent;
      }
    }

    // Horizontal rules
    :deep(hr) {
      margin: 24px 0;
      border: none;
      border-top: 1px solid var(--bs-gray-300);
    }

    // Images
    :deep(img) {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 16px 0;
      border-radius: 8px;
    }

    // Tables
    :deep(table) {
      width: 100%;
      margin: 16px 0;
      border-collapse: collapse;
      border: 1px solid var(--bs-gray-300);
    }

    :deep(th),
    :deep(td) {
      padding: 12px;
      text-align: left;
      border: 1px solid var(--bs-gray-300);
    }

    :deep(th) {
      background-color: var(--bs-gray-100);
      font-weight: 700;
      color: var(--bs-gray-900);
    }

    :deep(tr:nth-child(even)) {
      background-color: var(--bs-gray-50);
    }
  }
</style>
